/**
 * CapacityTable Component - Displays format breakdown with capacity data
 * Requirements: 2.1, 2.4, 2.5
 */

import { CapacityRow } from '../services/api';

interface CapacityTableProps {
  capacity: CapacityRow[];
}

function CapacityTable({ capacity }: CapacityTableProps) {
  const formatNumber = (num: number | null): string => {
    if (num === null) return '—';
    return num.toLocaleString();
  };

  const formatPercent = (num: number): string => {
    return `${(num * 100).toFixed(1)}%`;
  };

  const getUtilizationStyle = (utilization: number): React.CSSProperties => {
    if (utilization >= 1) {
      return { color: '#dc3545', fontWeight: 'bold' }; // Red for 100%
    }
    if (utilization >= 0.95) {
      return { color: '#fd7e14' }; // Orange for >95%
    }
    if (utilization < 0.7) {
      return { color: '#28a745' }; // Green for <70%
    }
    return {};
  };

  const getRowStyle = (utilization: number): React.CSSProperties => {
    if (utilization >= 1) {
      return { backgroundColor: '#fff5f5' }; // Light red background for full capacity
    }
    return {};
  };

  return (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Format</th>
            <th style={{ ...styles.th, ...styles.numericHeader }}>Planned Cases</th>
            <th style={{ ...styles.th, ...styles.numericHeader }}>Load Capacity</th>
            <th style={{ ...styles.th, ...styles.numericHeader }}>Open Capacity</th>
            <th style={{ ...styles.th, ...styles.numericHeader }}>Utilization</th>
          </tr>
        </thead>
        <tbody>
          {capacity.map((row) => (
            <tr key={row.format} style={getRowStyle(row.utilization)}>
              <td style={styles.td}>{row.format}</td>
              <td style={{ ...styles.td, ...styles.numeric }}>
                {formatNumber(row.plannedCases)}
              </td>
              <td style={{ ...styles.td, ...styles.numeric }}>
                {formatNumber(row.loadCapacity)}
              </td>
              <td style={{ ...styles.td, ...styles.numeric }}>
                {row.utilization >= 1 ? (
                  <span style={styles.noCapacity}>No Open Capacity</span>
                ) : (
                  formatNumber(row.openCapacity)
                )}
              </td>
              <td style={{ ...styles.td, ...styles.numeric, ...getUtilizationStyle(row.utilization) }}>
                {formatPercent(row.utilization)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={styles.totalRow}>
            <td style={{ ...styles.td, fontWeight: 'bold' }}>Total</td>
            <td style={{ ...styles.td, ...styles.numeric, fontWeight: 'bold' }}>
              {formatNumber(capacity.reduce((sum, r) => sum + r.plannedCases, 0))}
            </td>
            <td style={{ ...styles.td, ...styles.numeric, fontWeight: 'bold' }}>
              {formatNumber(capacity.reduce((sum, r) => sum + (r.loadCapacity || 0), 0))}
            </td>
            <td style={{ ...styles.td, ...styles.numeric, fontWeight: 'bold' }}>
              {formatNumber(capacity.reduce((sum, r) => sum + (r.openCapacity || 0), 0))}
            </td>
            <td style={{ ...styles.td, ...styles.numeric, fontWeight: 'bold' }}>
              {formatPercent(
                capacity.reduce((sum, r) => sum + r.plannedCases, 0) /
                capacity.reduce((sum, r) => sum + (r.loadCapacity || 0), 0) || 0
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #dee2e6',
    color: '#495057',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  numericHeader: {
    textAlign: 'right',
  },
  td: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #eee',
    verticalAlign: 'middle',
  },
  numeric: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  totalRow: {
    backgroundColor: '#f8f9fa',
  },
  noCapacity: {
    color: '#dc3545',
    fontSize: '0.8rem',
    fontStyle: 'italic',
  },
};

export default CapacityTable;
